<template>
  <v-container fluid>
    <v-card>
      <v-card-title
        class="d-flex align-center pa-4"
        style="background-color: #ffb500"
      >
        <v-icon :icon="mdiHistory" class="mr-2 text-white"></v-icon>
        <span class="text-h5 text-white">
          Historique des ajustements de points
        </span>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="selectedCustomerId"
              :items="customers"
              item-title="displayName"
              item-value="id"
              label="Filtrer par client"
              :prepend-icon="mdiAccount"
              clearable
              variant="outlined"
              density="comfortable"
              @update:modelValue="fetchHistory"
              @update:search="debouncedUpdateCustomers"
            >
              <template v-slot:selection="{ item }">
                {{ item.raw.number }} - {{ item.raw.name }}
              </template>
              <template v-slot:item="{ props, item }">
                <v-list-item v-bind="props">
                  <template v-slot:title>
                    {{ item.raw.number }} - {{ item.raw.name }}
                  </template>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="6" class="d-flex align-center">
            <v-btn
              color="#ffb500"
              :prepend-icon="mdiRefresh"
              class="text-white"
              @click="fetchHistory"
              :loading="loading"
            >
              Actualiser
            </v-btn>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="history"
          :loading="loading"
          :items-per-page="20"
          class="elevation-1"
        >
          <!-- Date -->
          <template v-slot:[`item.adjustmentDate`]="{ item }">
            {{ formatDate(item.adjustmentDate) }}
          </template>

          <!-- Client -->
          <template v-slot:[`item.customerName`]="{ item }">
            <div class="d-flex align-center">
              <v-icon :icon="mdiAccount" size="small" class="mr-2"></v-icon>
              <div>
                <div class="font-weight-medium">{{ item.customerName }}</div>
                <div class="text-caption text-medium-emphasis">
                  N°: {{ item.customerNumber }}
                </div>
              </div>
            </div>
          </template>

          <!-- Montant avec couleur -->
          <template v-slot:[`item.amount`]="{ item }">
            <v-chip
              :color="item.amount > 0 ? '#ffb500' : '#545857'"
              variant="flat"
              size="small"
            >
              <v-icon
                :icon="item.amount > 0 ? mdiPlus : mdiMinus"
                size="small"
                class="mr-1"
              ></v-icon>
              {{ Math.abs(item.amount) }} pts
            </v-chip>
          </template>

          <!-- Raison -->
          <template v-slot:[`item.reason`]="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <div
                  v-bind="props"
                  class="text-truncate"
                  style="max-width: 200px"
                >
                  {{ item.reason }}
                </div>
              </template>
              <span>{{ item.reason }}</span>
            </v-tooltip>
          </template>

          <!-- Administrateur -->
          <template v-slot:[`item.adminUserName`]="{ item }">
            <div class="d-flex align-center">
              <v-icon :icon="mdiAccountTie" size="small" class="mr-2"></v-icon>
              <span>{{ item.adminUserName || "N/A" }}</span>
            </div>
          </template>

          <!-- Détails -->
          <template v-slot:[`item.details`]="{ item }">
            <v-btn
              :icon="mdiInformationOutline"
              variant="text"
              size="small"
              @click="showDetails(item)"
            ></v-btn>
          </template>

          <!-- Actions suppression -->
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              :icon="mdiDelete"
              variant="text"
              size="small"
              color="error"
              @click="confirmDelete(item)"
            ></v-btn>
          </template>

          <!-- Message si pas de données -->
          <template v-slot:no-data>
            <v-alert type="info" variant="tonal" class="ma-4">
              Aucun ajustement de points trouvé
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog de détails -->
    <v-dialog v-model="detailsDialog" max-width="700px" scrollable>
      <v-card v-if="selectedItem">
        <v-card-title
          class="d-flex align-center pa-4"
          style="background-color: #ffb500"
        >
          <span class="text-h6 text-white">Détails de l'ajustement</span>
          <v-spacer></v-spacer>
          <v-btn
            :icon="mdiClose"
            variant="text"
            @click="detailsDialog = false"
            color="white"
          ></v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-list lines="two">
            <v-list-item>
              <template v-slot:prepend>
                <v-icon :icon="mdiCalendar"></v-icon>
              </template>
              <v-list-item-title>Date</v-list-item-title>
              <v-list-item-subtitle>{{
                formatDate(selectedItem.adjustmentDate)
              }}</v-list-item-subtitle>
            </v-list-item>

            <v-divider></v-divider>

            <v-list-item>
              <template v-slot:prepend>
                <v-icon :icon="mdiAccount"></v-icon>
              </template>
              <v-list-item-title>Client</v-list-item-title>
              <v-list-item-subtitle>
                {{ selectedItem.customerName }} (N°:
                {{ selectedItem.customerNumber }})
              </v-list-item-subtitle>
            </v-list-item>

            <v-divider></v-divider>

            <v-list-item>
              <template v-slot:prepend>
                <v-icon
                  :icon="selectedItem.amount > 0 ? mdiPlus : mdiMinus"
                  :color="selectedItem.amount > 0 ? '#ffb500' : '#545857'"
                ></v-icon>
              </template>
              <v-list-item-title>Montant</v-list-item-title>
              <v-list-item-subtitle>
                <strong
                  :style="{
                    color: selectedItem.amount > 0 ? '#ffb500' : '#545857',
                  }"
                >
                  {{ selectedItem.amount > 0 ? "+" : ""
                  }}{{ selectedItem.amount }}
                </strong>
                points
              </v-list-item-subtitle>
            </v-list-item>

            <v-divider></v-divider>

            <v-list-item>
              <template v-slot:prepend>
                <v-icon :icon="mdiTextBox"></v-icon>
              </template>
              <v-list-item-title>Raison</v-list-item-title>
              <v-list-item-subtitle class="text-wrap">{{
                selectedItem.reason
              }}</v-list-item-subtitle>
            </v-list-item>

            <v-divider></v-divider>

            <v-list-item>
              <template v-slot:prepend>
                <v-icon :icon="mdiAccountTie"></v-icon>
              </template>
              <v-list-item-title>Administrateur</v-list-item-title>
              <v-list-item-subtitle>{{
                selectedItem.adminUserName || "N/A"
              }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>

          <!-- Détails de répartition -->
          <v-expansion-panels class="mt-4">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <v-icon :icon="mdiChartTree" class="mr-2"></v-icon>
                Détails de répartition
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-list density="compact">
                  <v-list-item
                    v-if="selectedItem.franchiseGroup"
                    density="compact"
                  >
                    <v-list-item-title class="text-caption"
                      >Groupe de franchise:</v-list-item-title
                    >
                    <v-list-item-subtitle>{{
                      selectedItem.franchiseGroup
                    }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item v-if="selectedItem.franchise" density="compact">
                    <v-list-item-title class="text-caption"
                      >Franchise:</v-list-item-title
                    >
                    <v-list-item-subtitle>{{
                      selectedItem.franchise
                    }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item
                    v-if="selectedItem.subFranchise"
                    density="compact"
                  >
                    <v-list-item-title class="text-caption"
                      >Sous-franchise:</v-list-item-title
                    >
                    <v-list-item-subtitle>{{
                      selectedItem.subFranchise
                    }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item v-if="selectedItem.sapg" density="compact">
                    <v-list-item-title class="text-caption"
                      >SAPG:</v-list-item-title
                    >
                    <v-list-item-subtitle>{{
                      selectedItem.sapg
                    }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item
                    v-if="selectedItem.gimItemType"
                    density="compact"
                  >
                    <v-list-item-title class="text-caption"
                      >Type d'article GIM:</v-list-item-title
                    >
                    <v-list-item-subtitle>{{
                      selectedItem.gimItemType
                    }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item v-if="selectedItem.gimBrand" density="compact">
                    <v-list-item-title class="text-caption"
                      >Marque GIM:</v-list-item-title
                    >
                    <v-list-item-subtitle>{{
                      selectedItem.gimBrand
                    }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item
                    v-if="selectedItem.gimSubBrand"
                    density="compact"
                  >
                    <v-list-item-title class="text-caption"
                      >Sous-marque GIM:</v-list-item-title
                    >
                    <v-list-item-subtitle>{{
                      selectedItem.gimSubBrand
                    }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item
                    v-if="selectedItem.buyingGroupName"
                    density="compact"
                  >
                    <v-list-item-title class="text-caption"
                      >Groupe d'achat:</v-list-item-title
                    >
                    <v-list-item-subtitle>{{
                      selectedItem.buyingGroupName
                    }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item v-if="selectedItem.uom" density="compact">
                    <v-list-item-title class="text-caption"
                      >Unité de mesure:</v-list-item-title
                    >
                    <v-list-item-subtitle>{{
                      selectedItem.uom
                    }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="detailsDialog = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de confirmation de suppression -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">Confirmer la suppression</v-card-title>
        <v-card-text>
          <v-alert type="warning" variant="tonal" class="mb-4">
            Cette action est irréversible
          </v-alert>
          <p>Êtes-vous sûr de vouloir supprimer cet ajustement de points ?</p>
          <v-list v-if="itemToDelete" density="compact" class="mt-3">
            <v-list-item>
              <v-list-item-title>Client</v-list-item-title>
              <v-list-item-subtitle>
                {{ itemToDelete.customerName }} (N°:
                {{ itemToDelete.customerNumber }})
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>Montant</v-list-item-title>
              <v-list-item-subtitle>
                {{ itemToDelete.amount > 0 ? "+" : ""
                }}{{ itemToDelete.amount }} points
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>Date</v-list-item-title>
              <v-list-item-subtitle>
                {{ formatDate(itemToDelete.adjustmentDate) }}
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
          <p class="mt-3 text-caption text-medium-emphasis">
            Les points du contrat seront automatiquement recalculés après la
            suppression.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">Annuler</v-btn>
          <v-btn
            color="error"
            variant="elevated"
            @click="deleteOrder"
            :loading="deleting"
          >
            Supprimer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import paginations from "@/mixins/paginations";
import _ from "lodash";
import {
  mdiHistory,
  mdiAccount,
  mdiAccountTie,
  mdiRefresh,
  mdiPlus,
  mdiMinus,
  mdiInformationOutline,
  mdiClose,
  mdiCalendar,
  mdiTextBox,
  mdiChartTree,
  mdiDelete,
} from "@mdi/js";

export default {
  name: "PointAdjustmentHistory",
  mixins: [paginations],
  data() {
    return {
      // Icons
      mdiHistory,
      mdiAccount,
      mdiAccountTie,
      mdiRefresh,
      mdiPlus,
      mdiMinus,
      mdiInformationOutline,
      mdiClose,
      mdiCalendar,
      mdiTextBox,
      mdiChartTree,
      mdiDelete,

      // Data
      selectedCustomerId: null,
      detailsDialog: false,
      selectedItem: null,
      deleteDialog: false,
      itemToDelete: null,
      deleting: false,

      // Headers de la table
      headers: [
        { title: "Date", key: "adjustmentDate", sortable: true },
        { title: "Client", key: "customerName", sortable: true },
        { title: "Montant", key: "amount", sortable: true },
        { title: "Raison", key: "reason", sortable: false },
        { title: "Administrateur", key: "adminUserName", sortable: true },
        { title: "Détails", key: "details", sortable: false, align: "center" },
        { title: "Actions", key: "actions", sortable: false, align: "center" },
      ],
    };
  },
  created() {
    // Créer la méthode débouncée pour la recherche de clients
    this.debouncedUpdateCustomers = _.debounce(
      (item) => this.updateFields(item, "clients"),
      300,
    );
  },
  computed: {
    ...mapGetters("pointAdjustments", ["history", "loading"]),
    customers() {
      // Computed property qui se met à jour automatiquement avec le store
      return this.$store.state.clients.items.map((customer) => ({
        ...customer,
        displayName: `${customer.number} - ${customer.name}`,
      }));
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    /**
     * Méthode pour gérer les recherches dans les autocomplete
     */
    updateFields(item, search) {
      // Ne lancer la recherche que si on a une vraie valeur de recherche
      if (item && item.length >= 2) {
        this.searchBack(item, 500, search, false);
      }
    },
    ...mapActions("pointAdjustments", ["fetchHistory"]),
    ...mapActions("clients", { fetchClients: "fetchAll" }),

    /**
     * Charge les données initiales
     */
    async loadData() {
      try {
        // Charger une liste initiale de clients (la recherche dynamique affinera)
        await this.fetchClients();
        // Le computed customers se met à jour automatiquement

        // Charger l'historique complet
        await this.fetchHistory(null);
      } catch (error) {
        this.$store.commit("setSnackbar", {
          model: true,
          text: "Erreur lors du chargement des données",
          color: "error",
        });
      }
    },

    /**
     * Récupère l'historique (avec ou sans filtre)
     */
    async fetchHistory() {
      try {
        await this.$store.dispatch(
          "pointAdjustments/fetchHistory",
          this.selectedCustomerId,
        );
      } catch (error) {
        this.$store.commit("setSnackbar", {
          model: true,
          text: "Erreur lors de la récupération de l'historique",
          color: "error",
        });
      }
    },

    /**
     * Affiche les détails d'un ajustement
     */
    showDetails(item) {
      this.selectedItem = item;
      this.detailsDialog = true;
    },

    /**
     * Confirme la suppression d'un ajustement
     */
    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    /**
     * Supprime une commande d'ajustement
     */
    deleteOrder() {
      if (!this.itemToDelete || !this.itemToDelete.orderId) return;

      this.deleting = true;
      this.$store
        .dispatch("ordersOneStryker/deleteOrder", this.itemToDelete.orderId)
        .then(() => {
          this.$store.commit("setSnackbar", {
            model: true,
            text: "Ajustement de points supprimé avec succès. Les points ont été recalculés.",
            color: "success",
          });
          this.deleteDialog = false;
          this.itemToDelete = null;
          this.fetchHistory();
        })
        .catch((error) => {
          let errorMessage = "Erreur lors de la suppression de l'ajustement";

          if (error.response?.status === 403) {
            errorMessage =
              "Vous n'avez pas les droits pour effectuer cette action";
          } else if (error.response?.status === 404) {
            errorMessage =
              "Cet ajustement n'existe plus ou a déjà été supprimé";
          }

          this.$store.commit("setSnackbar", {
            model: true,
            text: errorMessage,
            color: "error",
          });
        })
        .finally(() => {
          this.deleting = false;
        });
    },

    /**
     * Formate une date
     */
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("fr-FR", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
  },
};
</script>

<style scoped>
.text-wrap {
  white-space: normal;
  word-break: break-word;
}
</style>
